// Loaded from CDN and included in externals in webpack.config.js
import MathJax from "mathjax";

// Required JS from node_modules
import React from "react";
import { createRoot } from "react-dom/client";

// Required JS from local files
import {
   addInitialFunction,
   Context,
   dfmAlert,
   dfmDialog,
   dfmTaskSetter,
   getAccuracyColour,
   isParent,
   isStudent,
   isTeacher,
   isTrustAdmin
} from "./dfm5";
import { answerSubmitHandler, defaultResponseFunction, dfm_generateQuestionContent } from "./question-util";
import { openVideo } from "./util-videos";
import CoursesDropdown from "~/components/general/CoursesDropdown";
import CoursesHomeLink from "~/components/general/CoursesHomeLink";
import ExplorerCourseAddBar from "~/components/pages/explorer/CourseAddBar";
import ExplorerCourseManageOptions from "~/components/pages/explorer/CourseManageOptions";
import {
   getCourse,
   getUnit,
   getCourseGroupsForDropdown,
   updateRecentlyViewedCourses
} from "~/utils/courses";
import {
   buildErrorMessageHtml,
   getErrorMessageHtmlForAjaxError
} from "~/utils/errors";
import { posthogEventCourseViewed } from "~/utils/posthog/courses";
import { posthogPageviewEvent } from "~/utils/posthog/general";
import { posthogEventResourceGroupSelected } from "~/utils/posthog/resources";
import { posthogEventLiveGameCreated } from "~/utils/posthog/livegame";
import { posthogEventTaskFixedQuestionsChosen } from "~/utils/posthog/tasks";

// CSS
import "./explorer.css";

/**
 * Gets the relevant data and renders the relevant parts of the explorer page
 * based on the given parameters.
 *
 * @param {{ coid?: number, cuid?: number, mode? string, skid?: number }} params A object containing URL query parameters.
 */
async function loadPageFromParams(params) {
   // Set the query parameters.
   const queryParams = params ?? {};
   const { coid, cuid, mode, skid } = queryParams;

   /**
    * The page needs to be rendered differently if the user has come from
    * manage-courses with the purpose of choosing a published course to
    * add to their selection.
    *
    * In this case, the "mode" query parameter will equal "preview".
    * Otherwise we can assume the user is viewing the explorer in the normal way.
    *
    * In preview mode:
    *
    * -  The user (if of appropriate type) should see the course manage options
    *    in a bar running along the top of the page.
    * -  The course dropdown should be in a disabled state.
    * -  The courses home link should be hidden from the user.
    * -  The user should not be able to select skills or set tasks.
    */
   const previewMode = mode === "preview";

   // Render the course home link and selection box if not in preview mode.
   if (!previewMode) {
      renderCoursesHomeLink();
      $("#explorer-selection").show();
   }

   // Render the preview message, if applicable.
   $("#explorer-course-components-preview-message").toggle(previewMode);

   // Render the loading message.
   loadExplorerLoading();

   // Get the course associated with the coid or cuid, if either have been set.
   let unit = cuid ? await getUnit(cuid) : null;
   const courseId = unit ? unit._module?.coid : coid ?? null;
   let course = courseId ? await getCourse(courseId) : null;

   // Only use unit/course with a relevant course status.
   if (course && ![1,3].includes(course.status)) {
      course = null;
      unit = null;
      const errorMessageHTML = buildErrorMessageHtml("This course is not currently available.");
      dfmAlert(`<div style="margin: 12px 0">${errorMessageHTML}</div>`);
   }

   // Reset the page state if there is no course and no unit.
   if (!course && !unit) {
      saveState({}, "Dr Frost Learning", "/explorer.php");
   }

   // Render the correct courses dropdown with any current course preselected.
   await renderCoursesDropdown({ preselectedCourse: course, previewMode });

   // Render the relevant course manage component.
   if (course) {
      renderCourseManageComponent({ course, previewMode });
   }

   /**
    * If a course is set but no unit is set, select the first unit of the
    * course if it exists and the screen width is large enough to display
    * the course contents.
    *
    * The breakpoint is currently set at 900px in explorer.css so we will
    * need to keep this in sync with that.
    */
   if (course && !unit && window.innerWidth > 900) {
      const firstUnit = course.modules?.[0]?.units?.[0] ?? null;
      unit = firstUnit ? await getUnit(firstUnit.cuid) : null;
   }

   // Render the modules of any current course.
   if (course?.modules) {
      renderCourseModules({
         modules: course.modules,
         currentUnitId: unit?.cuid,
         previewMode,
      });
   }

   // Either render the unit page or the explorer homepage.
   if (unit) {
      renderUnit({
         unit,
         scrollToSkid: skid,
         previewMode,
      });
   } else {
      loadExplorerHomepage();
   }

   // Update local storage if there is a user and course.
   if (Context.user && course) {
      const uid = Context.user.uid;
      updateRecentlyViewedCourses(uid, course);
   }
}

/**
 * Loads the parts of the page dependent on the query parameters before setting up the rest of the page.
 */
async function initialPageRender() {

   // Send a page view event.
   posthogPageviewEvent();

   // Also send a page view event if we've navigated here via the bfcache
   window.addEventListener('pageshow', (event) => {
     if (event.persisted) {
       posthogPageviewEvent();
     }
   });

   const queryParams = preloadState ?? {};
   window.history.replaceState(preloadState, null);
   await loadPageFromParams(queryParams)
      .then(() => {
         // Initially there will be no items selected, set classes accordingly.
         changeSelectionClasses("hide");

         // Show the complete skill list for appropriate user types.
         if (isTeacher() || isTrustAdmin()) $("#complete-skill-list").show();

         // Set the event handler on the back arrow.
         $("#explorer-topicheader .arrow-back").on("click", loadExplorerHomepage);

         // Set the appropriate options and event handlers on the selection panel.
         if (Context?.user && !isStudent()) {
            $("#explorer-selection-buttons").html("<button id='explorer-selection-settask' disabled>Set a Task</button>"
               +"<button class='empty' id='explorer-selection-generateworksheet' disabled>Generate Worksheet</button>"
               +"<button class='empty' id='explorer-selection-practise' disabled>Have a Go</button>");
            $("#explorer-selection-settask").on("click", function() {
               const selection = getBottomBarExplorerSelection(false, true, currentUnit?.data?.excludedssids ?? []);
               setTaskTypeChoice(selection);
            });
            $("#explorer-selection-generateworksheet").on("click", function(){
               const selection = getBottomBarExplorerSelection(true, true, currentUnit?.data?.excludedssids ?? []);
               worksheetDialog(selection, "explorer-generate-worksheet-generate-button");
            });
         } else {
            $("#explorer-selection-buttons").html("<button id='explorer-selection-practise' disabled>Practise</button>");
         }
         $("#explorer-selection-practise").on("click", function(){
            const selection = getBottomBarExplorerSelection(false, true, currentUnit?.data?.excludedssids ?? []);
            var taskType = getTaskType(selection, true);
            var recipients = Context.user ? {uids: [Context.user.uid]} : undefined; // if null, 'demo user' will be created by server
            dfmTaskSetter({
               isPractice : true,
               task : {
                        skills: selection.skills,
                        subskills: selection.subskills,
                        recipients: recipients,
                        type : taskType
                     },
               posthogTriggerId: "explorer-practise-start-button",
            });
         });

         // Make the explorer selection list sortable.
         $("#explorer-selection ul").sortable();

         // Setup the example mastery bars.
         $(".example-mastery-bars").each(function(){
            $(this).dfmMasteryBar();
         });
      });
}

// When document is ready and the user context is available, render the initial page.
$(() => {
   addInitialFunction(initialPageRender);
});

/**
 * Renders the explorer homepage with a message to select a course unit.
 */
function loadExplorerHomepage() {
   $("#explorer-home-message").text("Choose a course unit to get started");
   $("#explorer-home").show();
   $("#explorer-topic").hide();
   $("#explorer-main").removeClass("showingtopic");
   $(".explorer-strand ol li span").removeClass("selected");
}

/**
 * Renders the explorer homepage with a loading message.
 */
function loadExplorerLoading() {
   $("#explorer-home-message").text("Loading...");
   $("#explorer-home").show();
   $("#explorer-topic").hide();
   $("#explorer-main").removeClass("showingtopic");
}

/**
 * Works out the type code for the task.
 *
 * kc = independent practice with question generators only
 * c = independent practice with exam questions only
 * kt = teacher-set task with question generators only
 * t = teacher-set task with exam questions only
 *
 * A task with both types of subskills will only use question generators.
 */
function getTaskType(selection, isPractice) {
   const skills = selection.skills ?? [];
   const subskills = selection.subskills ?? [];
   const activeSubskills = subskills.filter(subskill => subskill.active);
   const subskillsFromSkills = skills.reduce((acc, skill) => acc.concat(skill.subskills), []);
   const activeSubskillsFromSkills = subskillsFromSkills.filter(subskill => subskill.active);
   const allActiveSubskills = [...activeSubskills, ...activeSubskillsFromSkills];
   const hasQuestionGeneratorSubskills = allActiveSubskills.some(subskill => subskill.type === "k");
   if (hasQuestionGeneratorSubskills) {
      return isPractice ? "kc" : "kt";
   }
   return isPractice ? "c" : "t";
}

/**
 * Renders the course module and unit structure, setting up relevant event handlers.
 */
function renderCourseModules({ modules = [], currentUnitId = null, previewMode }) {
   $("#explorer-topicselection-bycourse").empty();
   $.each(modules, function(_, module) {
      const units = module.units ?? [];
      $("#explorer-topicselection-bycourse").append(
            "<div class='explorer-strand' id='module-" + module.cmid + "'>"
         +        "<div class='explorer-strand-toggle'>"
         +           "<div>"
         +              "<img src='images/chevron_down.svg'>"
         +              "<h1>" + module.name + "</h1>"
         +           "</div>"
         +           "<span class='skill-count'>" + units.length + " units</span>"
         +        "</div>"
         +     "<ol></ol>"
         +  "</div>"
      );
      $("#module-" + module.cmid + " .explorer-strand-toggle").on("click", function(){
         $("#module-" + module.cmid + " ol").toggle();
      });
      const moduleContainsCurrentUnit = currentUnitId && units.find(u => u.cuid === currentUnitId);
      if (!moduleContainsCurrentUnit) {
         $("#module-" + module.cmid + " ol").toggle();
      }
      $.each(units, function(_, { cuid, name }) {
         $("#module-" + module.cmid + " ol").append(
            `<li id="unit-${cuid}"><span class="${currentUnitId === cuid ? "selected" : ""}">${name}</span></li>`
         );
         $("#unit-" + cuid).data('cuid', cuid);
         $("#unit-" + cuid).on("click", async function() {
            $(".explorer-strand ol li span").removeClass("selected");
            $(this).children().addClass("selected");
            const newState = previewMode ? { cuid, mode: "preview" } : { cuid };
            const newUrl = previewMode ? `?cuid=${cuid}&mode=preview` : `?cuid=${cuid}`;
            saveState(newState, "Explorer - " + name, newUrl);
            loadExplorerLoading();
            const unit = await getUnit(cuid);
            renderUnit({ unit, previewMode });
         });
      });
   });
   if (modules.length === 0) {
      $("#explorer-topicselection-bycourse").append(`
         <div style="color: grey; text-align:center;">
            This course has no modules
         </div>   
      `);
   }
}

/**
 * Returns an object representing the user selection from the explorer selection list.
 * 
 * @param {boolean} allowMixedSubskillTypes Allow a mixture of exam questions and question generator subskills.
 * @param {boolean} convertToSubskills Convert any whole skills into it's active subskills.
 * @param {number[]} ssidsToExclude Subskill IDs that should be excluded when converting a skill into subskills.
 * @returns {{ skills: Array, subskills: Array  }}
 */
function getBottomBarExplorerSelection(
   allowMixedSubskillTypes = false,
   convertToSubskills = false,
   ssidsToExclude = []
) {

   // Set up arrays for the return selection.
   let selectedSkills = [];
   let selectedSubskills = [];

   // Populate the selection from the jQuery data.
   $("#explorer-selection li").each(function() {
      if ($(this).data('subskill')) {
         selectedSubskills.push($(this).data('subskill'));
      } else {
         selectedSkills.push($(this).data('skill'));
      }
   });

   // Convert the skills to subskills if requested.
   if (convertToSubskills) {
      selectedSkills.forEach(skill => {
         // Set the subskills of this skill.
         const subskills = skill.subskills ?? [];

         /**
          * Keep subskills that are
          *    active
          *    not excluded
          */
         const validSubskills = subskills.filter(subskill => {
            const isActive = subskill.active;
            const isNotExcluded = !ssidsToExclude.some(ssid => ssid === subskill.ssid);
            return isActive && isNotExcluded
         });

         let subskillsToUse;

         if (allowMixedSubskillTypes) {
            subskillsToUse = validSubskills;
         } else {
            /**
             * If we have a mixture of subskill types, only keep
             * question generators.
             * 
             * This replicates the intention of the excludeExamSkills 
             * flag the previous code used.
             */
            const containsQuestionGenerators = validSubskills.some(subskill => subskill.type === "k");
            subskillsToUse = containsQuestionGenerators 
               ? validSubskills.filter(subskill => subskill.type === "k") 
               : validSubskills;
         }

         // Merge the subskills into the selection.
         selectedSubskills = [...selectedSubskills, ...subskillsToUse];
      })

      // Set the selected skills to an empty array now they have been converted.
      selectedSkills = [];
   }

   // Return the selection object.
   return { skills: selectedSkills, subskills: selectedSubskills };
}

function buildUnitResourceHTML(resource, type) {
   let resourceIconAlt;
   let resourceIconSrc;
   let resourceHref;
   let resourceType;
   let resourceTitle;
   switch (type) {
      case "worksheet":
         const { wid, name } = resource;
         resourceIconAlt = "document icon";
         resourceIconSrc = "/images/document_icon.svg";
         resourceHref = `/worksheets.php?wid=${wid}`;
         resourceType = "Online worksheet";
         resourceTitle = name;
         break;
      case "downloadable":
         const { rid, title } = resource;
         resourceIconAlt = "chart with graph icon";
         resourceIconSrc = "/images/downloadables_icon.svg";
         resourceHref = `/downloadables.php?rid=${rid}`;
         resourceType = "Downloadable resource";
         resourceTitle = title;
         break;
      case "url":
         const { url } = resource;
         resourceIconAlt = "open in new tab icon";
         resourceIconSrc = "/images/link_icon.svg";
         resourceHref = url;
         resourceType = "External link";
         resourceTitle = url;
         break;
      default:
         return;
   }
   const resourceHTML = $(`
      <a href="${resourceHref}" target="${type === "url" ? "_blank" : "_self"}">
         <div class="explorer-unit-resource">
            <img class="explorer-unit-resource-icon" alt="${resourceIconAlt}" src="${resourceIconSrc}">
            <div class="explorer-unit-resource-content">
               <span class="explorer-unit-resource-type">${resourceType}</span>
               <span class="explorer-unit-resource-title">${resourceTitle}</span>
            </div>
         </div>
      </a>
   `);

   // PostHog event tracking for resource selection.
   if (type === "downloadable") {
      resourceHTML.on("click", () => {
         posthogEventResourceGroupSelected({
            triggerId: "explorer-unit-downloadable-item",
            groupType: "resource",
            resource,
         });
      });
   }

   return resourceHTML;
}

let currentUnit;

/**
 * Renders the unit section of the page.
 */
function renderUnit({ unit, scrollToSkid = null, previewMode = false }) {

   currentUnit = unit;

   const resources = unit?._resources ?? [];
   const skills = unit?.skills ?? [];

   // Wire up each unit section toggle.
   $(".explorer-unit-section-toggle").each((_, element) => {
      $(element).off("click");
      $(element).on("click", () => {
         const currentToggleState = element.parentElement.dataset.state;
         element.parentElement.dataset.state = currentToggleState === "open" ? "closed" : "open";
         $(element).siblings(".explorer-unit-section-content").slideToggle({ duration: 300 });
      });
   });

   // Build the unit resources section.
   const maxResourceDisplay = skills.length === 0 ? resources.length : 2;
   $("#explorer-unit-resource-list").empty();
   const resourcesList = $("#explorer-unit-resource-list");
   resources.slice(0, maxResourceDisplay).forEach(({ resource, type }) => {
      const resourceHTML = buildUnitResourceHTML(resource, type);
      resourcesList.append(resourceHTML);
   });
   $("#explorer-unit-resource-list-show-all").toggle(resources.length > maxResourceDisplay);
   $("#explorer-unit-resource-list-show-all").off("click").on("click", () => {
      $("#explorer-unit-resource-list-show-all").hide();
      resources.slice(maxResourceDisplay, resources.length).forEach(({ resource, type }) => {
         const resourceHTML = buildUnitResourceHTML(resource, type);
         resourcesList.append(resourceHTML);
      });
   });

   // Build the unit skills section.
   $("#explorer-skilllist").empty();
   $.each(skills, function(k, skill) {
      const subskills = Array.isArray(skill.subskills) ? skill.subskills : [];
      const hasActiveSubskills = subskills.some(ss => ss.active);
      const skillDisabled = previewMode || !hasActiveSubskills;

      $("#explorer-skilllist").append("<div id='explorer-skill-"+skill.skid+"' class='skill'></div>");
      $("#explorer-skill-"+skill.skid).data('skill', skill);
      $("#explorer-skill-"+skill.skid).append(
            "<span class='mastery-bar'>" + (skill.mastery ? skill.mastery : 0) + "</span>"
         +  "<h1>"
         +     "<label style='" + ( skillDisabled ? "" : "cursor: pointer;" ) + "'>"
         +        ( skillDisabled ? "<input type='checkbox' style='opacity: 0' disabled>" : "<input type='checkbox'>" )
         +        "<em>" + skill.publicid + "</em>"
         +        skill.name
         +     "</label>"
         +  "</h1>"
         +  "<h2>Mastery: " + (skill.mastery ? Math.round(skill.mastery) : "0" ) + "/100</h2>"
         +  "<table class='data'><thead><th>Or Narrow Down</th><th></th><th>Video</th><th>Difficulty</th><th>Recent Accuracy</th></thead><tbody></tbody></table>"
      );
      $("#explorer-skill-"+skill.skid+" .mastery-bar").dfmMasteryBar();  // skill.mastery

      $("#explorer-skill-"+skill.skid+" input[type=checkbox]").on("change", function() {
         if ($(this).is(':checked')) {
            addSkillToSelection($(this).closest('div').data('skill'));
         } else {
            removeSkillFromSelection($(this).closest('div').data('skill').skid);
         }
      });

      $.each(subskills, function(k2, ss) {
         ss.publicid = skill.publicid; // make numbering of skill available inside subskill
         var useSubskill = true;

         // Still use checkbox on inactive rows for consistent spacing, but make it transparent and disabled
         const showCheckbox = ss.active && !skillDisabled;
         const checkbox = showCheckbox ? "<input type='checkbox'>" : "<input type='checkbox' style='opacity: 0' disabled>";
         const rowClass = `class='${ss.active ? "active" : "inactive"}${showCheckbox ? "" : " disabled"}'`;
         const rowTitle = ss.active ? "" : "title='This subskill is currently in development and will be available for use in the near future.'";
         const rowClassAndTitle = `${rowClass} ${rowTitle}`;

         if(unit.data && unit.data.excludedssids && $.inArray(ss.ssid, unit.data.excludedssids) !== -1) useSubskill = false;
         if(useSubskill)$("#explorer-skill-"+skill.skid+" tbody").append("<tr id='explorer-subskill-"+ss.ssid+"'" + rowClassAndTitle + "><td><label>" + checkbox + "<span>" + skill.publicid+(ss.letter ? ss.letter : "")+": <strong>"+ss.name+"</strong></span></label></td>"
            +((Context.user && !isStudent() && !isParent()) && ss.type === 'e' ?
                  (ss.active ? "<td><a href='#' class='browse-questions'>Browse</a></td>" : "<td></td>") :
                  (ss.active ? "<td><a href='#' class='example-question'>Example</a></td>" : "<td></td>"))
            +"<td><button class='video-button'></button></td>"
            +"<td>"+(ss.difficulty ? ss.difficulty : "1-4")+"</td>"
            +"<td>"+(ss.accuracy ? ss.accuracy+"%" : "")+"</td>"
            +"</tr>");
         $("#explorer-subskill-"+ss.ssid).data("subskill", ss);
         $("#explorer-subskill-"+ss.ssid+" .example-question").click(function(e){
            e.stopPropagation();
            var subskill = $(this).closest('tr').data('subskill');
            console.log("[ExampleClick] ssid = "+subskill.ssid);
            showExampleQuestion(subskill, $("#explorer-subskill-"+subskill.ssid+" label").html());
            return false;
         });
         $("#explorer-subskill-"+ss.ssid+" .browse-questions").click(function(){
            window.location = 'browse.php?skids=['+$(this).closest('tr').data('subskill').skid+"]";
            return false;
         });
         $("#explorer-subskill-"+ss.ssid+" .video-button").click(function(){ 
            var subskill = $(this).closest('tr').data('subskill');
            if (subskill.video) {
               openVideo({
                  subskill,
                  posthogTriggerId: "explorer-subskill-video-icon"
               });
            } else {
               dfmAlert("Apologies, but a video isn't yet available for this subskill. We're working on new ones all the time and hope to make this available soon.");
            }
            return false;
         });
         if(!ss.video)$("#explorer-subskill-"+ss.ssid+" .video-button").prop('disabled', true);
         $("#explorer-subskill-"+ss.ssid+" input[type=checkbox]").change(function() {
            if($(this).is(':checked'))addSubskillToSelection($(this).closest('tr').data('subskill'));
            else removeSubskillFromSelection($(this).closest('tr').data('subskill').ssid);
         });
         if(ss.accuracy) {
            $("#explorer-subskill-"+ss.ssid+" td:last-child").css('font-weight', '1000').css('color', getAccuracyColour(ss.accuracy)).css('border-left', '1px solid '+getAccuracyColour(ss.accuracy));
         }
      });
   });

   // Set up the initial display state of resources and skills.
   const showResources = resources.length > 0;
   const showSkills = skills.length > 0;
   const startResourcesOpen = showResources;
   const startSkillsOpen = showSkills;
   $("#explorer-unit-resources").toggle(showResources);
   $("#explorer-unit-skills").toggle(showSkills);
   $("#explorer-unit-resources").children(".explorer-unit-section-content").toggle(startResourcesOpen);
   $("#explorer-unit-skills").children(".explorer-unit-section-content").toggle(startSkillsOpen);
   $("#explorer-unit-resources")[0].dataset.state = startResourcesOpen ? "open" : "closed";
   $("#explorer-unit-skills")[0].dataset.state = startSkillsOpen ? "open" : "closed";
   $('#explorer-unit-empty-message').toggle(!showResources && !showSkills);

   // Build the unit header.
   $("#explorer-topicheader h1").html(unit?.name);
   $("#explorer-topicheader h2").html("");
   $.each(unit?.pathcache, function(i, p){
      $("#explorer-topicheader h2").append(p.name+" &rarr; ");
   });
   $("#explorer-topicheader").show();

   // Any skills already selected we need to reselect the checkboxes?
   var selection = getBottomBarExplorerSelection();
   if(selection.skills)$.each(selection.skills, function(k,skill){
      $("#explorer-skill-"+skill.skid+" h1 input[type=checkbox]").prop('checked', true);
   });
   if(selection.subskills)$.each(selection.subskills, function(k,subskill){
      $("#explorer-subskill-"+subskill.ssid+" input[type=checkbox]").prop('checked', true);
   });

   $("#explorer-home").hide();
   $("#explorer-topic").show();
   $("#explorer-main").addClass('showingtopic');

   // If a skill id was provided, scroll to it.
   const scrollTop = scrollToSkid ? $("#explorer-skill-" + scrollToSkid).offset().top - 110 : 0;
   $([document.documentElement, document.body]).animate({
      scrollTop
   }, 300);

   MathJax.typeset();
}

/**
 * Modifies the classes related to having or not having anything selected.
 */
function changeSelectionClasses(action) {
   if (action === "show") {
      $("#explorer-selection").removeClass('noselection');
      $("#explorer-topic").addClass('explorer-selection-padding');
      $("#explorer-topicselection").addClass('explorer-selection-padding');
   } else if (action === "hide") {
      $("#explorer-selection").addClass('noselection');
      $("#explorer-topic").removeClass('explorer-selection-padding');
      $("#explorer-topicselection").removeClass('explorer-selection-padding');
   }
}

function addSubskillToSelection(subskill) {
   $("#explorer-selection ul").append("<li id='selection-"+subskill.ssid+"'><img src='images/menu_grip_white.svg'><a href='#' class='remove'>&times;</a> <label>"
           +subskill.publicid+(subskill.letter ? subskill.letter : "")+"</label><strong>"+subskill.name+"</strong></li>");
   $("#explorer-selection #selection-"+subskill.ssid+" .remove").click(function(){
      removeSubskillFromSelection(subskill.ssid);
      return false;
   });
   $("#explorer-selection #selection-"+subskill.ssid).data('subskill', subskill);
   $("#explorer-selectioninstructions").hide();
   $("#explorer-selection-buttons button").prop('disabled', false);
   // Unselect parent skill if selected
   removeSkillFromSelection(subskill.skid);
   changeSelectionClasses("show");
   MathJax.typeset();
}

function removeSubskillFromSelection(ssid) {
   $("#explorer-selection #selection-"+ssid).remove();
   $("#explorer-subskill-"+ssid+" input[type=checkbox]").prop('checked', false);
   if($("#explorer-selection ul").children().length==0) {
      $("#explorer-selectioninstructions").show();
      $("#explorer-selection-buttons button").prop('disabled', true);
      changeSelectionClasses("hide");
   }
}

function addSkillToSelection(skill) {
   $("#explorer-selection ul").append("<li id='selection-sk"+skill.skid+"'><img src='images/menu_grip_white.svg'><a href='#' class='remove'>&times;</a> <label>"
           +skill.publicid+"</label><strong>"+skill.name+"</strong></li>");
   $("#explorer-selection #selection-sk"+skill.skid+" .remove").click(function(){
      removeSkillFromSelection(skill.skid);
      return false;
   });
   $("#explorer-selection #selection-sk"+skill.skid).data('skill', skill);
   $("#explorer-selectioninstructions").hide();
   $("#explorer-selection-buttons button").prop('disabled', false);

   // Unselect any subskills
   $("#explorer-skill-"+skill.skid+" tbody input[type=checkbox]").prop('checked', false).change();

   changeSelectionClasses("show");
   MathJax.typeset();
}

function removeSkillFromSelection(skid) {
   $("#explorer-selection #selection-sk"+skid).remove();
   $("#explorer-skill-"+skid+" h1 input[type=checkbox]").prop('checked', false);
   if($("#explorer-selection ul").children().length==0) {
      $("#explorer-selectioninstructions").show();
      $("#explorer-selection-buttons button").prop('disabled', true);
      changeSelectionClasses("hide");
   }
}

function showExampleQuestion(subskill, title) {
   dfmAlert("<div id='explorer-example-container'><button id='refreshexample'><img src='/images/refresh_icon.svg'></button><button id='fullscreenexample'><img src='/images/fullscreen_icon.svg'></button></div>"
        +(title ? "<h2 style='font-weight:400' id='example-title'>"+title+"</h2>" : "") + "<div id='example-question'></div>");
   loadExampleQuestion(subskill);
   $("#refreshexample").on("click", function(){
      loadExampleQuestion(subskill);
   });
   $("#fullscreenexample").on("click", function(){
      window.open("/do-question.php?ssid="+subskill.ssid);
   });
}

function loadExampleQuestion(subskill) {
   var url = subskill.type === "k" ? "/api/questions/keyskill/" + subskill.ssid : "/api/questions/examskill/" + subskill.ssid;
   $.ajax({
      url: url,
      context: document.body,
      dataType: "json",
      success: function(data){
         $("#example-title").find('input[type=checkbox]').remove();
         $("#example-question").data("question", data);
         dfm_generateQuestionContent($("#example-question"), data, function(){ return answerSubmitHandler($("#example-question"),
            function(submitData){
              defaultResponseFunction($("#example-question"))(submitData);
              $('input[type=submit]').prop('disabled', false);
            }
         )});

         MathJax.typeset();
      },
      error: function(jqXHR){
         $("#example-title").find('input[type=checkbox]').remove();
         $("#example-title").css("margin-bottom", 0);
         $("#explorer-example-container").hide();
         const errorMessageHTML = getErrorMessageHtmlForAjaxError(jqXHR, "There was an error retrieving this example question.");
         $("#example-question").html(`<div style="padding: 24px 0 16px 0;">${errorMessageHTML}</div>`);
      }
   });
}

function setTaskTypeChoice(selection) {

   // Change they haven't mixed Key Skills and exam practice.
   var hasKS = false; var hasEP = false;
   if(selection.subskills)$.each(selection.subskills, function(k,subskill) {
      if(subskill.type=="k")hasKS = true;
      else hasEP = true;
   });

   var inputHTML = "<h1>Set a Task</h1>";
   if(hasEP && hasKS)inputHTML+= "<p class='notice-light'><strong>Note:</strong> As you mixed Exam Practice and Question Generators, the 'Flexible Questions' option is not available.</p>";
   inputHTML+= "<ul class='dialog-skillselection'>"+getSelectionAsString(selection)+"</ul>";
   inputHTML+= "<div id='dialog-skillselection-tasktypechoice'>";
   inputHTML+= "<div><label><input type='radio' checked name='task-homeworktype' value='fixed' id='task-homeworktype-fixed'> Fixed Questions<br><small>Advantages: Everyone gets the same questions. Option to set as a formal assessment.</small></label></div>";
   if(!(hasEP && hasKS))inputHTML+= "<div><label><input type='radio' name='task-homeworktype' value='nonfixed' id='task-homeworktype-nonfixed'> Flexible Questions<br><small>Advantages: More control over completion criteria, e.g. accuracy required. Differentiate by ability, with students advancing to harder subskills in your selection as they consistently get questions correct.</small></label></div>";
   inputHTML+= "<div><label><input type='radio' name='task-homeworktype' value='live' id='task-homeworktype-live'> Live! Game<br><small>Intended for a classroom environment. Students see the questions on your own screen and play along on their mobile/tablet device.</small></label></div>";
   inputHTML+= "</div>";
   dfmDialog(inputHTML, [{label: "Continue", action: function(){
      var choice = $("input[name=task-homeworktype]:checked").val();
      if (choice === "fixed") {
         worksheetDialog(selection, "explorer-fixed-questions-generate-button");
      } else if (choice === "live") {
         const subskills = selection.subskills;
         startLiveGame(subskills);
      } else {
         var task = { type: getTaskType(selection, false), skills: selection.skills, subskills: selection.subskills };
         dfmTaskSetter({
            isPractice: false,
            task,
            posthogTriggerId: "explorer-flexible-questions-set-button",
         });
      }
   }}]);
   MathJax.typeset();
}

function startLiveGame(subskills) {
   // If there are any generated subskills, remove any exam question subskills.
   if(!Context.user) {
      dfmAlert("This functionality is available only to registered users. <a href='register.php'>Click to register</a>.");
      return;
   }
   var inputHTML = "";
   inputHTML+= "<strong>Select an option for participants</strong><br><small>This allows you to control whether login is required to join the game, and whether participants are allowed to use a custom nickname.</small>";
   inputHTML+= "<select name='livemode' style='margin-top:10px'><option value='0'>DFM login required (no nicknames)</option><option value='1'>DFM login required (nicknames allowed)</option><option value='2'>Guests allowed</option>"+((Context.user.type=='teacher' || Context.user.type=='admin') ? "<option value='3'>Select a class</option>" : "")+"</select>";
   if(Context.user.type=='teacher' || Context.user.type=='admin') {
      inputHTML+= "<input type='hidden' name='liveclass'>";
   }
   inputHTML+= "<br><br><strong>Select a points mode</strong><br><small>In the normal mode, the fastest correct answer gets 1000 points, with a minimum of 500 for a correct answer.</small>";
   inputHTML+= "<br><select name='pointsmode' style='margin-top:10px'><option value='0' selected>Faster answers get more points</option><option value='1'>No speed bonus</option></select>";
   inputHTML+= "<br><br><strong>What appears on student screens?</strong><br><small>If you're doing the game remotely, choose for the question to appear on your students' screens.</small>";
   inputHTML+= "<br><select id='studentdisplay' name='studentdisplay' style='margin-top:10px'><option value='0' selected>Answer input only</option><option value='1'>Question and answer input</option></select>";
   inputHTML+= "<br><br><strong>How many questions?</strong><br><select name='numquestions'>";
   $.each([4,6,8,10,12,15,20,30], function(k,v) {
      inputHTML+= "<option value='"+v+"'>"+v+"</option>";
   });
   inputHTML+= "</select>";

   dfmDialog(inputHTML+"<br><br>", [{label: "Start Game", action: function(){
      const options = {
         ssids: subskills.map(ss => ss.ssid),
         numquestions: Number($("select[name=numquestions]").val()),
         entrymode: Number($("select[name=livemode]").val()),
         pointsmode: Number($("select[name=pointsmode]").val()),
         studentdisplay: Number($("select[name=studentdisplay]").val())
      };
      if ($("select[name=livemode]").val() == 3) {
         options.cid = JSON.parse($("input[name='liveclass']").val()).cids[0];
      }
      $.ajax({
         url: "/api/live/startgame",
         type: "POST",
         context: document.body,
         dataType: "json",
         data: JSON.stringify(options),
         success: function(data) {
            if (data.gid) {
               posthogEventLiveGameCreated({
                  triggerId: "explorer-start-live-game-button",
                  gid: data.gid,
                  options: {
                     ...options,
                     subskills
                  },
               });
            }
            window.location = "live-hub.php";
         },
         error: function(j, textStatus, errorThrown){
            dfmAlert("There was an error deleting this worksheet/template:<br><br><strong>"+j.responseText+"</strong>");
         }
      });
   }}]);

   $("input[name=liveclass]").studentSelector({"classesOnly": true});

   $("select[name=livemode]").change(function(){
      if($(this).val()==3)$("input[name=liveclass]").next().show();
      else $("input[name=liveclass]").next().hide();
   }).change();
}

function getSelectionAsString(selection) {
   var inputHTML = "";
   if(selection.skills) {
      $.each(selection.skills, function(k,sk) {
         inputHTML+= "<li>"+sk.publicid+": <strong>"+sk.name+"</strong></li>";
      });
   }
   if(selection.subskills) {
      $.each(selection.subskills, function(k,ss) {
         inputHTML+= "<li>"+ss.publicid+(ss.letter?ss.letter:"")+": <strong>"+ss.name+"</strong></li>";
      });
   }
   return inputHTML;
}

function worksheetDialog(selection, posthogTriggerId = null) {
   console.log("[WorksheetDialog] selection = "+JSON.stringify(selection));
   var inputHTML = "<h1>Generate Worksheet</h1>";
   inputHTML+= "<ul class='dialog-skillselection'>"+getSelectionAsString(selection)+"</ul>";
   inputHTML+= "<p>This facility, using the skills you selected, allows you to create a fixed set of questions that you can either <strong>set as a homework/assessment</strong> or <strong>export to Word</strong> as a worksheet.</p>";
   // A skill contains subskills. A selection of a skill implies inclusion of ALL subskills within it.
   var subskillCount = 0;
   if(selection.subskills)subskillCount+= selection.subskills.length;
   if(selection.skills)$.each(selection.skills, function(k,sk) {
      subskillCount+= sk.subskills.length;
   });

   inputHTML+= "<label style='display:inline-block;width:140px'>Num Questions:</label> <select name='numquestions'>";
   for(var i=1; i<=30; i++)if(i<=6 || i%2==0)inputHTML+= "<option value='"+(i*subskillCount)+"'>"+i+" of each subskill ("+(i*subskillCount)+" total)</option>";
   inputHTML+= "</select>";
   if (subskillCount>1) {
      inputHTML += "<br><br><label style='display:inline-block;width:140px'>Interleave Skills:</label>"
       + "<select name='interleave'>"
       + "<option value='0'>No</option><option value='1' selected>Yes</option>"
       + "</select><br>"
       + "<p style='font-size: 12px; color: #262626; padding-bottom: 8px;'>When set to Yes, questions will rotate between the chosen subskills. If set to No, questions of the same subskills will appear together.</p>";
   }
   dfmDialog(inputHTML, [{label: "Generate", action: function(){
      const numberOfQuestions = Number($("select[name=numquestions]").val());
      const interleave = Number($("select[name=interleave]").val());
      if (posthogTriggerId) {
         posthogEventTaskFixedQuestionsChosen({
            triggerId: posthogTriggerId,
            numberOfSubskills: subskillCount,
            numberOfQuestionsPerSubskill: numberOfQuestions / subskillCount,
            interleave: interleave === 1,
         });
      }
      let data = { numquestions: numberOfQuestions };
      if (subskillCount > 1) {
         data.interleave = interleave;
      }
      var subskillIds = [];
      $.each(selection.subskills, function(k,v) { subskillIds.push(v.ssid) });
      data.ssids = subskillIds;
      window.location = "worksheets.php?wtid=new&data="+encodeURIComponent(JSON.stringify(data));
      return;
   }}]);
   MathJax.typeset();
}

// --------------------------------------
// -- Page resizing
// --------------------------------------

$(window).on("resize", function(){
   var height = $(window).height() - $("#top-bar").outerHeight();
   $("#explorer-topicselection").outerHeight(height);
});

// --------------------------------------
// -- Page state management
// --------------------------------------

window.onpopstate = function(e) {
   if (e.state) {
      loadPageFromParams({ coid: e.state.coid, cuid: e.state.cuid, skid: e.state.skid });
   } else {
      loadPageFromParams()
   }
};

function saveState(state, title, url) {
   document.title = title;
   window.history.replaceState(state, null, url);
}

// React courses dropdown component.
const coursesDropdownRoot = createRoot(document.getElementById("explorer-react-courses-dropdown"));
async function renderCoursesDropdown({ preselectedCourse = null, previewMode = false }) {
   const courseGroups = await getCourseGroupsForDropdown({
     user: Context?.user,
     preselectedCourse,
   });

   const handleChange = async (courseIdString) => {
      const coid = parseInt(courseIdString);
      const course = await getCourse(coid);
      saveState({ coid }, `Explorer - ${course?.name}`, `?coid=${coid}`);

      // Update local storage if there is a user and a course.
      if (Context.user && course) {
         const uid = Context.user.uid;
         updateRecentlyViewedCourses(uid, course);
      }

      const courseGroupsWithoutCurrentlyViewing = courseGroups.filter(
         ({ heading }) => heading !== "Currently viewing",
      );

      /**
         * Render the component with the updated props
         * https://react.dev/reference/react-dom/client/createRoot#updating-a-root-component
         */
      coursesDropdownRoot.render(
         <CoursesDropdown
            courseGroups={courseGroupsWithoutCurrentlyViewing}
            onChange={handleChange}
            value={coid}
         />
      );
      renderCourseModules({
         modules: course?.modules ?? [],
         previewMode,
      });
      renderCourseManageComponent({
         course,
         previewMode
      });
      loadExplorerHomepage();

      // PostHog event.
      posthogEventCourseViewed({
         triggerId: "explorer-courses-dropdown",
         course,
      });
   };

   coursesDropdownRoot.render(
      <CoursesDropdown
         disabled={previewMode}
         courseGroups={courseGroups}
         onChange={handleChange}
         startOpen={!preselectedCourse}
         value={preselectedCourse ? preselectedCourse.coid : null}
      />
   );
}

// React courses home link component.
const coursesHomeLinkRoot = createRoot(document.getElementById("explorer-react-courses-home-link"));
function renderCoursesHomeLink() {
   document.getElementById("explorer-react-courses-home-link").style.display = "block";
   coursesHomeLinkRoot.render(
      <CoursesHomeLink
         href="/courses-redirect.php"
         posthogTriggerId="explorer-courses-home-button"
      />
   );
}

// React courses manage options component.
const courseManageOptionsRoot = createRoot(document.getElementById("explorer-react-course-manage-options"));
function renderCourseManageOptions(props) {
   document.getElementById("explorer-react-course-manage-options").style.display = "block";
   courseManageOptionsRoot.render(<ExplorerCourseManageOptions {...props} />);
}

// React courses add bar component.
const courseAddBarRoot = createRoot(document.getElementById("explorer-react-course-add-bar"));
function renderCourseAddBar(props) {
   courseAddBarRoot.render(<ExplorerCourseAddBar {...props} />);
}

// Renders the relevant manage component for the course and mode, if applicable to the user.
function renderCourseManageComponent({ course, previewMode = false }) {
   const userHasSchool = !!Context.user?.sid;
   const userCanAddOrAdapt = ["admin", "teacher", "trust"].includes(Context.user?.type);
   if (course && userHasSchool && userCanAddOrAdapt) {
      const courseAdded = course.inSchoolSelection;
      const baseProps = {
         course,
         courseAdded,
         user: Context.user,
      }
      if (previewMode) {
         // Render the manage options in a bar along the top.
         if (!courseAdded) {
            const props = {
               ...baseProps,
               buildPosthogTriggerId: (action) => `explorer-top-bar-${action}-button`,
               onCourseAdd: () => window.location = "/manage-courses.php",
            }
            renderCourseAddBar(props);
         }
      } else {
         // Render the manage options underneath the course dropdown.
         const props = {
            ...baseProps,
            buildPosthogTriggerId: (action) => `explorer-left-panel-${action}-button`,
            linkClassName: "h-8 p-1.5 text-xs",
            onCourseAdd: () => {
               renderCoursesDropdown({ preselectedCourse: course }),
               renderCourseManageOptions({ ...props, courseAdded: true });
            },
         }
         renderCourseManageOptions(props);
      }
   }
}
